import createAxiosInstance from './axiosConfig';
import { NegociacaoEstornoDTO } from '../shared/dto/negociacaoEstornoDTO';
import { ManualLancamentoDTO } from '../shared/dto/manualLancamentoDTO';
import { ComissaoParametrosDTO } from '../shared/dto/comissaoParametrosDTO';
import { ComissaoParametrosGeraisDTO } from '../shared/dto/comissaoParametrosGeraisDTO';
import { ComissaoPerfilDTO } from '../shared/dto/comissaoPerfilDTO';

// Interfaces auxiliares para retorno
export interface ParceiroSaldoDTO {
    id: number;
    nome: string;
    tipo: 'PROMOTORA' | 'REGIONAL' | 'COMERCIAL';
    saldoDevedorTotal: number;
}

export interface NegociacaoDTO {
    id: number;
    idParceiro: number;
    nomeNegociacao: string;
    valorTotal: number;
    saldoRestante: number;
    qtdParcelas: number;
    dataCriacao: string;
    status: string;
    parcelas?: any[]; 
}

// Interface para Configuração CNAB 240
export interface ParametrosCnabDTO {
    id?: number;
    // Empresa
    empresaNome: string;
    empresaCnpj: string;
    empresaLogradouro: string;
    empresaNumero: string;
    empresaCidade: string;
    empresaCep: string;
    empresaUf: string;
    // Banco
    bancoCodigo: string;
    bancoNome: string;
    agencia: string;
    agenciaDv: string;
    conta: string;
    contaDv: string;
    convenio: string;
}

// Ajuste conforme sua URL base real
const { REACT_APP_ESTEIRA_BACK } = process.env;

// Instância para o Controller Principal de Comissões
const axiosComissao = createAxiosInstance(REACT_APP_ESTEIRA_BACK + '/rest/comissaoController');

// Instância para o Controller de Faturas
const axiosFaturas = createAxiosInstance(REACT_APP_ESTEIRA_BACK + '/rest/faturasController');

export default class ComissaoService {
    axios: any;
    axiosFaturas: any;

    constructor() {
        this.axios = axiosComissao;
        this.axiosFaturas = axiosFaturas;
    }

    // --- MÉTODOS CNAB (NOVO) ---

    /**
     * Busca a configuração atual do CNAB (Empresa Pagadora e Banco).
     * Endpoint: GET /financeiro/cnab/config
     */
    async buscarConfigCnab() {
        return this.axios.get('/financeiro/cnab/config');
    }
    

    /**
     * Salva ou atualiza a configuração do CNAB.
     * Endpoint: POST /financeiro/cnab/config
     */
    async salvarConfigCnab(dto: ParametrosCnabDTO) {
        return await this.axios.post('/financeiro/cnab/config', dto);
    }

    // --- MÉTODOS DE FATURAS ---

    async gerarFaturaIndividual(inicio: string, fim: string, idOrigem: number, tipoOrigem: string) {
        return await this.axiosFaturas.post('/gerar-individual', null, {
            params: { inicio, fim, idOrigem, tipoOrigem }
        });
    }

    async gerarRemessaCnab(idsFaturas: number[], idContaBancaria?: number) {
        let url = '/financeiro/cnab/gerar-remessa';
        
        // Adiciona o ID da conta como Query Param se ele for passado
        if (idContaBancaria) {
            url += `?contaId=${idContaBancaria}`;
        }

        return this.axios.post(url, idsFaturas, {
            responseType: 'blob'
        });
    }

    /**
     * Atualiza o status de uma ou múltiplas faturas (Aprovação em Lote).
     * Endpoint: PATCH /rest/comissaoController/status-lote
     */
    async atualizarStatusEmLote(ids: number[], status: string, observacao: string = "") {
        // Envia o DTO { ids, status, observacao } criado no Backend
        return await this.axios.patch('/status-lote', {
            ids,
            status,
            observacao
        });
    }

    // --- MÉTODOS DE NEGOCIAÇÃO E SALDO ---

    async listarParceirosDevedores() {
        return await this.axios.get('/financeiro/devedores');
    }

    async listarNegociacoesPorParceiro(idParceiro: number) {
        return await this.axios.get(`/financeiro/negociacoes/${idParceiro}`);
    }

    async baixarParcela(idParcela: number) {
        return await this.axios.patch(`/financeiro/parcelas/${idParcela}/baixar`);
    }

    async liquidarNegociacaoTotal(idNegociacao: number) {
        return await this.axios.post(`/financeiro/negociacoes/${idNegociacao}/liquidar`);
    }

    async negociarEstorno(dto: NegociacaoEstornoDTO | any) { 
        return await this.axios.post(`/financeiro/negociar`, dto);
    }

    async aceitarNegociacaoParceiro(idNegociacao: number) {
        return await this.axios.post(`/negociacoes/${idNegociacao}/aceitar`);
    }

    // --- MÉTODOS GERAIS ---

    async listarComissoesPendentes(idParceiro: number) {
        return await this.axios.get(`/financeiro/extrato/${idParceiro}`);
    }

    async uploadNotaFiscal(idCalculo: number, numero: string, valor: number, arquivo: File) {
        const formData = new FormData();
        formData.append('numero', numero);
        formData.append('valor', valor.toString());
        formData.append('arquivo', arquivo);

        return await this.axios.post(`/notas/upload/${idCalculo}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    }

    async aprovarNota(idNota: number) {
        return await this.axios.patch(`/notas/${idNota}/aprovar`);
    }

    async reprovarNota(idNota: number, motivo: string) {
        return await this.axios.patch(`/notas/${idNota}/reprovar`, motivo, {
            headers: { 'Content-Type': 'text/plain' }
        });
    }

    async realizarEstorno(idCalculo: number, motivo: string, tipoOperacao: number = 3) {
        const url = `/financeiro/estornar/${idCalculo}/${tipoOperacao}?motivo=${encodeURIComponent(motivo)}`;
        
        return await this.axios.post(url);
    }

    async buscarSaldoDevedor(idParceiro: number) {
        return await this.axios.get(`/financeiro/extrato/${idParceiro}`);
    }

    async obterExtratoFinanceiro(idParceiro: number) {
        return await this.axios.get(`/financeiro/extrato/${idParceiro}`);
    }

    async registrarLancamentoManual(dto: ManualLancamentoDTO) {
        return await this.axios.post(`/financeiro/lancamento-manual`, dto);
    }

    async listarRegras() {
        return await this.axios.get('/parametros');
    }

    async buscarRegraPorId(id: number) {
        return await this.axios.get(`/parametros/${id}`);
    }

    async excluirRegrasEmLote(ids: number[]) {
        return await this.axios.delete('/parametros/lote', { data: ids });
    }

    async salvarRegra(dto: ComissaoParametrosDTO) {
        return await this.axios.post('/parametros', dto);
    }

    async excluirRegra(id: number) {
        return await this.axios.delete(`/parametros/${id}`);
    }

    async buscarParametrosGerais() {
        return await this.axios.get('/financeiro/parametros-gerais');
    }

    async salvarParametrosGerais(dto: ComissaoParametrosGeraisDTO) {
        return await this.axios.post('/financeiro/parametros-gerais', dto);
    }

    async listarPerfis() {
        return await this.axios.get('/perfil');
    }

    async salvarPerfil(dto: ComissaoPerfilDTO) {
        return await this.axios.post('/perfil', dto);
    }

    async baixarRelatorioConsolidado(inicio: string, fim: string, idsPromotoras: number[], detalhado: boolean = false) {
        return await this.axiosFaturas.get('/fatura/consolidado', {
            params: {
                inicio,
                fim,
                idsPromotoras: idsPromotoras.join(','), // Converte [1,2,3] para "1,2,3"
                detalhado
            },
            responseType: 'blob' // Necessário para processar o download do PDF
        });
    }
}